// /vms/devices — 帳號 VMS 裝置總覽（ls-loop MV.4，docs/mycam/mv-spec.md ⑤）。
// 一頁看帳號底下每台攝影機：在線（最後聯絡）、儲存（SD 卡 / 手機空間）、錄影（設定 + 是不是真的在錄）、韌體（有沒有新版）；
// 有問題的排最上面（離線 > 儲存 > 錄影停了 > 韌體 > 正常）。AI 電腦另一區。
// 資料：裝置清單（在線 / 錄影設定 / 韌體）+ 在線攝影機讀屬性（0x8090 儲存、0x8050 錄影狀態）；每 30 s 更新。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getEdgeDevices } from '../../lib/api';
import { edgeOnline } from '../../lib/eventAi';
import { formatRelativeUs, formatTimestampUs } from '../../lib/format';
import { isPhoneCam } from '../../lib/phoneCam';
import { overviewOf, sortRows, type Health, type IssueKey, type LiveAttrs, type OverviewRow } from '../../lib/vms/overview';
import { signaling } from '../../lib/ws';
import { cameraClass, deviceName, tileState, type DeviceSummary } from '../devices/deviceModel';
import { REC_STATUS, SD_CAPACITY, SD_PRESENT, SD_STATUS } from '../devices/settings/ids';
import { frameValue, opGet } from '../devices/settings/ops';
import { useDeviceList } from '../devices/useDeviceList';
import './vms.css';

const REFRESH_MS = 30_000;
const ATTR_TIMEOUT_MS = 8_000;

const hasCluster = (d: DeviceSummary, c: string) => (d.capability_effective ?? []).some((x) => x.cluster?.toLowerCase() === c);

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([p, new Promise<T>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms))]);
}

async function waitOpen(ms: number): Promise<void> {
  if (signaling.getState() === 'open') return;
  signaling.connect();
  await withTimeout(
    new Promise<void>((resolve) => {
      const off = signaling.on('state', (s) => {
        if (s === 'open') {
          off();
          resolve();
        }
      });
    }),
    ms
  );
}

/** 讀一台在線攝影機的儲存 / 錄影狀態；讀不到的欄位不帶（→ unknown，不告警）。 */
async function readLiveAttrs(d: DeviceSummary): Promise<LiveAttrs> {
  const id = d.device_id;
  await waitOpen(ATTR_TIMEOUT_MS);
  await withTimeout(signaling.subscribe(id), ATTR_TIMEOUT_MS);
  const get = (attr: string) => withTimeout(opGet(id, attr), ATTR_TIMEOUT_MS).then(frameValue);
  const storage = hasCluster(d, '0x8090');
  const [present, status, cap, rec] = await Promise.allSettled([
    storage ? get(SD_PRESENT) : Promise.reject(new Error('n/a')),
    storage ? get(SD_STATUS) : Promise.reject(new Error('n/a')),
    storage ? get(SD_CAPACITY) : Promise.reject(new Error('n/a')),
    hasCluster(d, '0x8050') ? get(REC_STATUS) : Promise.reject(new Error('n/a')),
  ]);
  void signaling.unsubscribe(id).catch(() => {});
  const v = <T,>(r: PromiseSettledResult<unknown>, ok: (x: unknown) => x is T): T | undefined => (r.status === 'fulfilled' && ok(r.value) ? r.value : undefined);
  const isBoolish = (x: unknown): x is boolean | number => typeof x === 'boolean' || typeof x === 'number';
  const toBool = (x: boolean | number | undefined) => (x === undefined ? undefined : x === true || x === 1);
  return {
    sdPresent: toBool(v(present, isBoolish)),
    sdStatus: v(status, (x): x is number => typeof x === 'number'),
    sdCapacity: v(cap, (x): x is string => typeof x === 'string'),
    recording: toBool(v(rec, isBoolish)),
  };
}

function Dot({ h, label, testId }: { h: Health; label: string; testId?: string }) {
  return (
    <span className={`vms-ov-health vms-ov-health--${h}`} data-health={h} data-testid={testId}>
      <span className="vms-ov-dot" aria-hidden />
      {label}
    </span>
  );
}

export default function DeviceOverviewPage() {
  const { t } = useTranslation();
  const { devices, reload } = useDeviceList();
  const cameras = useMemo(() => (devices ?? []).filter((d) => cameraClass(d) === 'camera'), [devices]);
  const [attrs, setAttrs] = useState<Record<string, LiveAttrs>>({});
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const camsRef = useRef(cameras);
  camsRef.current = cameras;

  const readAll = useCallback(async () => {
    const online = camsRef.current.filter((d) => tileState(d) === 'online');
    const res = await Promise.allSettled(online.map((d) => readLiveAttrs(d).then((a) => [d.device_id, a] as const)));
    const next: Record<string, LiveAttrs> = {};
    for (const r of res) if (r.status === 'fulfilled') next[r.value[0]] = r.value[1];
    setAttrs(next);
    setUpdatedAt(Date.now() * 1000);
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
      await readAll();
    } finally {
      setRefreshing(false);
    }
  }, [reload, readAll]);

  // 清單第一次到了就讀；之後每 30 s 全部重來
  const loaded = devices !== null;
  useEffect(() => {
    if (!loaded) return;
    void readAll();
    const h = setInterval(() => void refresh(), REFRESH_MS);
    return () => clearInterval(h);
  }, [loaded, readAll, refresh]);

  const rows = useMemo(
    () =>
      sortRows(
        cameras.map((d) => ({
          d,
          name: deviceName(d),
          row: overviewOf({
            online: tileState(d) === 'online',
            recordMode: d.recording?.mode,
            recordLocation: d.recording?.location,
            needsUpgrade: d.firmware_upgrade?.needs_upgrade === true,
            hasStorage: hasCluster(d, '0x8090'),
            // 只有雲端錄影方案（cloud-*）才算「佔名額」；手機攝影機的 v1-standard 等不是錄影方案
            cloudPlan: d.subscription?.status === 'active' && (d.subscription?.plan ?? '').startsWith('cloud'),
            attrs: attrs[d.device_id],
          }),
        }))
      ),
    [cameras, attrs]
  );
  const edges = loaded ? getEdgeDevices<DeviceSummary>() : [];
  const attention = rows.filter((r) => r.row.rank < 4).length;

  const issueText = (k: IssueKey, d: DeviceSummary) => {
    const phone = isPhoneCam(d.model);
    if (k === 'firmware') return t('deviceOverview.issue_firmware', { v: d.firmware_upgrade?.latest_version ?? '' });
    if (k === 'planUnused') return t('deviceOverview.issue_planUnused', { plan: d.subscription?.plan ?? '' });
    return t(`deviceOverview.issue_${k}${phone && (k === 'noCard' || k === 'storageFail') ? '_phone' : ''}`);
  };

  const storageLabel = (r: OverviewRow, d: DeviceSummary) => {
    const phone = isPhoneCam(d.model);
    if (r.noCard) return t(phone ? 'deviceOverview.noCard_phone' : 'deviceOverview.noCard'); // 含回報 0 / 0 GB
    if (r.storage === 'unknown') return '—';
    if (r.issues.includes('noCard')) return t(phone ? 'deviceOverview.noCard_phone' : 'deviceOverview.noCard');
    if (r.issues.includes('storageFail')) return t('deviceOverview.abnormal');
    if (r.capacity) return t('deviceOverview.free', { free: r.capacity.freeGB, total: r.capacity.totalGB });
    return r.storage === 'ok' ? t('deviceOverview.normal') : '—';
  };

  const recLabel = (r: OverviewRow, d: DeviceSummary) => {
    const mode = d.recording?.mode || 'none';
    if (mode === 'none') return t('deviceOverview.recOff');
    const where = d.recording?.location === 'cloud' ? t('deviceOverview.recCloud') : isPhoneCam(d.model) ? t('deviceOverview.recPhone') : t('deviceOverview.recSd');
    const m = t(`deviceOverview.recMode_${mode}`, { defaultValue: mode });
    const now = r.recording === 'fail' ? t('deviceOverview.recStopped') : attrs[d.device_id]?.recording ? t('deviceOverview.recNow') : '';
    return [m, where, now].filter(Boolean).join(' · ');
  };

  return (
    <section className="vms-ov">
      <header className="vms-ec-head">
        <h1 style={{ marginTop: 0 }}>{t('deviceOverview.title')}</h1>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="text-secondary vms-lb-msg" data-testid="ov-updated">
            {updatedAt ? t('deviceOverview.updated', { time: formatTimestampUs(updatedAt, { year: undefined, month: undefined, day: undefined }) }) : t('common.loading')}
          </span>
          <Link className="btn-ghost" to="/vms/activity" data-testid="ov-activity-link">{t('vmsActivity.link')} →</Link>
          <button className="btn-ghost" onClick={() => void refresh()} disabled={refreshing} data-testid="ov-refresh">
            {refreshing ? t('common.loading') : t('deviceOverview.refresh')}
          </button>
        </span>
      </header>
      <p className="text-secondary vms-ec-sub" data-testid="ov-summary">
        {loaded ? (attention ? t('deviceOverview.attention', { count: attention, total: rows.length }) : t('deviceOverview.allGood', { total: rows.length })) : ''}
      </p>

      <div className="vms-ov-scroll card">
        <table className="vms-ov-table" data-testid="ov-table">
          <thead>
            <tr>
              <th>{t('deviceOverview.col_camera')}</th>
              <th>{t('deviceOverview.col_online')}</th>
              <th>{t('deviceOverview.col_storage')}</th>
              <th>{t('deviceOverview.col_recording')}</th>
              <th>{t('deviceOverview.col_firmware')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ d, name, row }) => (
              <tr key={d.device_id} data-testid={`ov-row-${d.device_id}`} data-rank={row.rank} className={row.rank < 4 ? 'vms-ov-attn' : undefined}>
                <td>
                  <Link to={`/devices/${encodeURIComponent(d.device_id)}`}>{name}</Link>
                  <span className="text-secondary vms-ov-sub">{d.model_name || d.model}</span>
                  {row.issues.length > 0 && (
                    <span className="vms-ov-issues" data-testid={`ov-issues-${d.device_id}`}>
                      {row.issues.map((k) => issueText(k, d)).join(t('ai.sep'))}
                    </span>
                  )}
                </td>
                <td>
                  <Dot h={row.online} label={row.online === 'ok' ? t('deviceOverview.online') : t('deviceOverview.offline')} />
                  {d.online?.last_seen ? <span className="text-secondary vms-ov-sub">{t('deviceOverview.lastSeen', { ago: formatRelativeUs(d.online.last_seen) })}</span> : null}
                </td>
                <td>
                  <Dot h={row.storage} label={storageLabel(row, d)} testId={`ov-storage-${d.device_id}`} />
                </td>
                <td>
                  <Dot h={row.recording} label={recLabel(row, d)} testId={`ov-rec-${d.device_id}`} />
                </td>
                <td>
                  <Dot h={row.firmware} label={d.firmware_version ?? '—'} />
                  {row.firmware === 'warn' && <span className="vms-ov-sub">{t('deviceOverview.newFw', { v: d.firmware_upgrade?.latest_version ?? '' })}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {edges.length > 0 && (
        <>
          <h2 className="vms-ov-h2">{t('deviceOverview.edgeTitle')}</h2>
          <div className="vms-ov-scroll card">
            <table className="vms-ov-table" data-testid="ov-edge-table">
              <tbody>
                {edges.map((e) => {
                  const on = edgeOnline(e); // 只看 state 會被舊的「在線」騙（最後聯絡超過 5 分鐘 = 離線）
                  return (
                    <tr key={e.device_id} data-testid={`ov-edge-${e.device_id}`}>
                      <td>{deviceName(e)}</td>
                      <td>
                        <Dot h={on ? 'ok' : 'warn'} label={on ? t('deviceOverview.online') : t('deviceOverview.offline')} />
                        {e.online?.last_seen ? <span className="text-secondary vms-ov-sub">{t('deviceOverview.lastSeen', { ago: formatRelativeUs(e.online.last_seen) })}</span> : null}
                      </td>
                      <td className="text-secondary">{e.firmware_version ?? ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-secondary vms-lb-msg">{t('deviceOverview.edgeHint')}</p>
        </>
      )}
    </section>
  );
}
